import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import formatDate from '../../../helpers/format-date';
import IconRight from '../../icons/right';
import BlacklistUserInfoGeneralBlock from './user-info-general-block';
import {
  addressesColumns,
  aliasesColumns,
  countryAssociationsColumns,
  identificationsColumns,
  relationshipsColumns,
} from './variables/general';
import { EntityRelationships } from '../types';
import { BlacklistUserInfoGeneralTypes } from './types';

import cn from './blacklist.module.css';

const formatRelationships = (
  list: EntityRelationships[],
): EntityRelationships[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};
    render.entityRelationshipGUID = (
      <div>
        <Link
          target="_blank"
          to={`/blocklist/list/users?entityGUID=${item.relatedEntityGUID}`}
          className={cn.viewLink}
        >
          View
          <IconRight />
        </Link>
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const BlacklistUserInfoGeneral = ({ info }: BlacklistUserInfoGeneralTypes) => {
  const formattedRelationshipsData = useMemo(() => {
    const data = formatRelationships(
      info?.source?.entityRelationships as EntityRelationships[],
    );

    return data;
  }, [info?.source?.entityRelationships]);

  return (
    <div className={cn.userInfoContent}>
      <div className={cn.userInfoContentBlock}>
        <BlacklistUserInfoGeneralBlock
          info={info?.source?.entityAddresses}
          columns={addressesColumns}
          title={`Addresses (${info?.source?.entityAddresses?.length || 0})`}
        />
      </div>
      <div className={cn.userInfoContentBlock}>
        <BlacklistUserInfoGeneralBlock
          info={info?.source?.entityAliases}
          columns={aliasesColumns}
          title={`Aliases (${info?.source?.entityAliases?.length || 0})`}
        />
      </div>
      <div className={cn.userInfoContentBlock}>
        <BlacklistUserInfoGeneralBlock
          info={info?.source?.entityCountryAssociations}
          columns={countryAssociationsColumns}
          title={`Country Associations (${
            info?.source?.entityCountryAssociations?.length || 0
          })`}
        />
      </div>
      <div className={cn.userInfoContentBlock}>
        <div className={`${cn.userInfoGeneralTitle} ${cn.withMargin}`}>
          Dates of Birth
        </div>
        {(!info?.source?.entityDOBs || !info?.source?.entityDOBs?.length) && (
          <div className={cn.dobWrapper}>
            <div className={cn.dobValue}>—</div>
          </div>
        )}
        {info?.source?.entityDOBs &&
          info?.source?.entityDOBs?.length > 0 &&
          info?.source?.entityDOBs?.map((item, index) => (
            <div
              className={cn.dobWrapper}
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.entityDOBGUID}-${index}-${item.birthDate.birthYear}`}
            >
              <div className={cn.dobValue}>
                {`${item.birthDate.birthYear || 'xxxx'}-${
                  item.birthDate.birthMonth &&
                  Number(item.birthDate.birthMonth) < 10
                    ? '0'
                    : ''
                }${item.birthDate.birthMonth || 'xx'}-${
                  item.birthDate.birthDay &&
                  Number(item.birthDate.birthDay) < 10
                    ? '0'
                    : ''
                }${item.birthDate.birthDay || 'xx'}`}
              </div>
              <div className={cn.dobValue}>
                {`Updated: ${
                  item?.lastUpdated ? formatDate(item.lastUpdated) : '—'
                }`}
              </div>
            </div>
          ))}
      </div>
      <div className={cn.userInfoContentBlock}>
        <BlacklistUserInfoGeneralBlock
          info={info?.source?.entityIdentifications}
          columns={identificationsColumns}
          title={`Identifications (${
            info?.source?.entityIdentifications?.length || 0
          })`}
        />
      </div>
      <div className={cn.userInfoContentBlock}>
        <div className={`${cn.userInfoGeneralTitle} ${cn.withMargin}`}>
          Positions
        </div>
        {(!info?.source?.entityPositions ||
          !info?.source?.entityPositions?.length) && (
          <div className={cn.dobWrapper}>
            <div className={cn.dobValue}>—</div>
          </div>
        )}
        {info?.source?.entityPositions &&
          info?.source?.entityPositions?.length > 0 &&
          info?.source?.entityPositions?.map((item) => (
            <div className={cn.dobWrapper} key={item.entityPositionGUID}>
              <div className={cn.dobValue}>{item?.lastUpdated}</div>
              <div className={cn.dobValue}>{item?.position}</div>
            </div>
          ))}
      </div>
      <div className={cn.userInfoContentBlock}>
        <BlacklistUserInfoGeneralBlock
          info={formattedRelationshipsData}
          columns={relationshipsColumns}
          title={`Relationships (${
            info?.source?.entityRelationships?.length || 0
          })`}
        />
      </div>
      <div className={cn.userInfoContentBlock}>
        <div className={`${cn.userInfoGeneralTitle} ${cn.withMargin}`}>
          Sources & Remarks
        </div>
        {(!info?.source?.entityRemarks ||
          !info?.source?.entityRemarks?.length) &&
          (!info?.source?.entitySourceItems ||
            !info?.source?.entitySourceItems?.length) && (
            <div className={cn.dobWrapper}>
              <div className={cn.dobValue}>—</div>
            </div>
          )}
        {info?.source?.entityRemarks &&
          info?.source?.entityRemarks?.length > 0 &&
          info?.source?.entityRemarks?.map((item) => (
            <div className={cn.sourcesItem} key={item.entityRemarkGUID}>
              <div className={cn.sourceItemLeft}>
                <div className={cn.sourceItemLeftValue}>Remark:</div>
                <div className={cn.sourceItemLeftValue}>{item.remark}</div>
              </div>
              <div
                className={cn.sourceItemRight}
              >{`(${item.lastUpdated})`}</div>
            </div>
          ))}
        {info?.source?.entitySourceItems &&
          info?.source?.entitySourceItems?.length > 0 &&
          info?.source?.entitySourceItems?.map((item) => (
            <div className={cn.sourcesItem} key={item.entitySourceItemGUID}>
              <div className={cn.sourceItemLeft}>
                <div className={cn.sourceItemLeftValue}>Source:</div>
                <div className={cn.sourceItemLeftValue}>
                  <a
                    href={item.sourceURI}
                    target="_blank"
                    rel="noreferrer"
                    className={cn.sourceLink}
                  >
                    {item.sourceURI}
                  </a>
                </div>
              </div>
              <div
                className={cn.sourceItemRight}
              >{`(${item.lastUpdated})`}</div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default BlacklistUserInfoGeneral;
